import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../i18n/dateFnsLocale';
import { KioskKeypad } from '../components/kiosk/KioskKeypad';
import { PinDots } from '../components/kiosk/PinDots';
import { KioskLanguageSelector } from '../components/kiosk/KioskLanguageSelector';

// API client helper that supplies the kiosk token header
const getKioskClient = (token: string) => {
  return axios.create({
    baseURL: import.meta.env.VITE_API_URL || '',
    headers: {
      'Content-Type': 'application/json',
      'X-Kiosk-Token': token,
    },
  });
};

// Maps backend error messages to i18n translation keys so the kiosk
// ALWAYS displays translated messages instead of raw English from the API.
const getKioskErrorMessage = (
  err: any,
  t: (key: string) => string,
  context: 'activation' | 'pin' | 'clockAction',
): string => {
  const backendMessage = err?.response?.data?.error?.message || '';

  if (backendMessage.includes('Incorrect PIN code') || backendMessage.includes('PIN code is required')) {
    return t('kiosk.ui.invalidPinCode');
  }
  if (backendMessage.includes('Invalid subdomain or activation code')) {
    return t('kiosk.ui.invalidOrExpiredActivationCode');
  }
  if (backendMessage.includes('already checked in')) {
    return t('kiosk.ui.userAlreadyCheckedIn');
  }
  if (backendMessage.includes('does not belong')) {
    return t('kiosk.ui.userNotInOrganization');
  }
  if (backendMessage.includes('No active check-in')) {
    return t('kiosk.ui.noActiveCheckIn');
  }
  if (backendMessage.includes('Invalid or inactive kiosk token') || err?.response?.status === 401) {
    return t('kiosk.ui.invalidOrInactiveToken');
  }

  // Context-specific fallback for unknown errors
  if (context === 'activation') return t('kiosk.ui.invalidOrExpiredActivationCode');
  if (context === 'pin') return t('kiosk.ui.invalidPinCode');
  return t('kiosk.ui.errorProcessingClockIn');
};

// Memoized Clock — only this subtree re-renders every second
const KioskClock = memo(({ paused, language }: { paused: boolean; language: string }) => {
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  useEffect(() => {
    if (paused) return; // Don't run interval while user is typing PIN
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [paused]);

  const dateLocale = getDateFnsLocale(language);

  return (
    <div className="text-right">
      <div className="text-2xl font-mono font-bold leading-none tracking-tight">
        {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>
      <div className="text-xs text-slate-400 font-medium mt-1">
        {format(currentTime, 'EEEE, d MMMM yyyy', { locale: dateLocale })}
      </div>
    </div>
  );
});
KioskClock.displayName = 'KioskClock';

// Memoized Enter button — prevents re-render on every keypress.
// `onPress` is stable (uses ref), `disabled` only changes when pin length
// crosses 4 or loading toggles — not on every keypress.
const KioskEnterButton = memo(({ onPress, disabled, loading, label }: { onPress: () => void; disabled: boolean; loading: boolean; label: string }) => (
  <button
    onPointerDown={(e) => { e.preventDefault(); if (!disabled) onPress(); }}
    disabled={disabled}
    className="w-full mt-4 h-14 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 rounded-2xl font-bold text-lg shadow-lg active:scale-[0.98] transition-transform transition-colors flex items-center justify-center"
  >
    {loading ? (
      <div className="h-6 w-6 border-2 border-indigo-200 border-t-white rounded-full animate-spin" />
    ) : (
      label
    )}
  </button>
));
KioskEnterButton.displayName = 'KioskEnterButton';

// Formats a shift duration from two ISO timestamps into a human-readable string.
// Handles: <1m, Ym only, Xh Ym, and Xd Yh Zm for edge cases over 24h.
const formatShiftDuration = (checkInIso: string, checkOutIso: string): string => {
  const checkInMs = new Date(checkInIso).getTime();
  const checkOutMs = new Date(checkOutIso).getTime();
  const diffMs = checkOutMs - checkInMs;

  if (diffMs <= 0) return '0m';

  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes === 0) return '<1m';

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
};

// Memoized floating error overlay — slides down from top, auto-dismisses.
// Absolute positioning ensures NO layout space is occupied, keeping the keypad
// in a fixed position so it fits on iPad without scrolling.
// Uses transform + opacity only (GPU-accelerated, iPad-friendly).
const KioskErrorOverlay = memo(({ message, visible, top }: { message: string; visible: boolean; top: string }) => (
  <div
    style={{ top }}
    className={`absolute left-0 right-0 z-30 flex justify-center px-4 pointer-events-none transition-all duration-300 ease-out ${
      visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-6'
    }`}
  >
    <div className="w-full max-w-md p-3 bg-red-950/80 border border-red-500/30 rounded-xl text-red-300 text-sm font-semibold text-center shadow-lg">
      {message}
    </div>
  </div>
));
KioskErrorOverlay.displayName = 'KioskErrorOverlay';

export const KioskPage: React.FC = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const currentLang = i18nInstance.language;

  // --- SUBDOMAIN / PATH EXTRACTION ---
  const getSubdomain = () => {
    const host = window.location.hostname;
    const urlParams = new URLSearchParams(window.location.search);
    const qParam = urlParams.get('subdomain');
    if (qParam) return qParam;
    
    if (host.includes('.checkin.')) {
      return host.split('.checkin.')[0];
    }
    return 'demo-org';
  };

  const [subdomain] = useState(getSubdomain);

  // --- STATE ---
  const [isActivated, setIsActivated] = useState<boolean>(false);
  const [kioskToken, setKioskToken] = useState<string>('');
  const [activationCode, setActivationCode] = useState<string>('');
  const [orgName, setOrgName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [errorVisible, setErrorVisible] = useState<boolean>(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivatePin, setDeactivatePin] = useState('');
  
  // PIN clock-in flow states
  const [userPin, setUserPin] = useState<string>('');
  const [step, setStep] = useState<'PIN' | 'USER_CONFIRM' | 'SUCCESS'>('PIN');
  
  // Verified user states
  const [verifiedUser, setVerifiedUser] = useState<any>(null);
  const [isCheckedIn, setIsCheckedIn] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [actionSuccessMsg, setActionSuccessMsg] = useState<string>('');
  const [shiftDuration, setShiftDuration] = useState<string>('');

  const pinTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clockActionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs to avoid regenerating handleKeypadPress on every PIN change (iPad optimization)
  const userPinRef = useRef(userPin);
  userPinRef.current = userPin;
  const stepRef = useRef(step);
  stepRef.current = step;

  // Cleanup timeout refs on unmount
  useEffect(() => {
    return () => {
      if (pinTimeoutRef.current) clearTimeout(pinTimeoutRef.current);
      if (clockActionTimeoutRef.current) clearTimeout(clockActionTimeoutRef.current);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, []);

  // Auto-dismiss floating error overlay after 3.5 seconds
  useEffect(() => {
    if (errorMsg) {
      setErrorVisible(true);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => {
        setErrorVisible(false);
        setErrorMsg('');
      }, 3500);
    } else {
      setErrorVisible(false);
    }
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, [errorMsg]);

  // --- CHECK LOCAL STORAGE FOR TOKEN ---
  useEffect(() => {
    const savedToken = localStorage.getItem(`kiosk_token_${subdomain}`);
    const savedOrgName = localStorage.getItem(`kiosk_org_${subdomain}`) || '';
    
    if (savedToken) {
      setKioskToken(savedToken);
      setOrgName(savedOrgName);
      setIsActivated(true);
      
      // Verify token is still valid on backend
      const client = getKioskClient(savedToken);
      client.get('/api/kiosk/info')
        .then((res) => {
          if (res.data?.success) {
            setOrgName(res.data.data.organizationName);
            localStorage.setItem(`kiosk_org_${subdomain}`, res.data.data.organizationName);
          }
        })
        .catch(() => {
          // Token invalid, require re-activation
          localStorage.removeItem(`kiosk_token_${subdomain}`);
          setIsActivated(false);
        });
    }
  }, [subdomain]);

  // --- SUBMIT ACTIVATION ---
  const handleActivate = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (activationCode.length !== 6) {
      setErrorMsg(t('kiosk.ui.activationCodeMustBe6Digits'));
      return;
    }
    
    setLoading(true);
    setErrorMsg('');
    
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await axios.post(`${apiUrl}/api/kiosk/activate`, {
        subdomain,
        activationCode,
      });

      if (response.data?.success) {
        const { kioskToken: token, organizationName, kioskSubdomain } = response.data.data;
        const activeSubdomain = kioskSubdomain || subdomain;
        localStorage.setItem(`kiosk_token_${activeSubdomain}`, token);
        localStorage.setItem(`kiosk_org_${activeSubdomain}`, organizationName);
        setKioskToken(token);
        setOrgName(organizationName);
        setIsActivated(true);
        setActivationCode('');

        // Dynamically update the URL query parameter to the real subdomain for seamless testing
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('subdomain') !== activeSubdomain) {
          urlParams.set('subdomain', activeSubdomain);
          window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
        }
      }
    } catch (err: any) {
      setErrorMsg(getKioskErrorMessage(err, t, 'activation'));
    } finally {
      setLoading(false);
    }
  }, [activationCode, subdomain, t]);

  // --- VERIFY PIN ---
  const verifyUserPin = useCallback(async (pinCode: string) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const client = getKioskClient(kioskToken);
      const response = await client.post('/api/kiosk/verify-pin', { pin: pinCode });
      
      if (response.data?.success) {
        const { user, isCheckedIn: checked } = response.data.data;
        setVerifiedUser(user);
        setIsCheckedIn(checked);
        setStep('USER_CONFIRM');
        setUserPin(''); // clear PIN input for security
      }
    } catch (err: any) {
      setErrorMsg(getKioskErrorMessage(err, t, 'pin'));
      setUserPin(''); // reset on failure
    } finally {
      setLoading(false);
    }
  }, [kioskToken, t]);

  // --- PIN KEYPAD HANDLING ---
  // Uses refs for userPin and step to keep this callback stable across re-renders (iPad optimization)
  const handleKeypadPress = useCallback((val: string) => {
    setErrorMsg('');
    if (stepRef.current !== 'PIN') return;

    if (val === 'clear') {
      setUserPin('');
    } else if (val === 'delete') {
      setUserPin(prev => prev.slice(0, -1));
    } else {
      const currentPin = userPinRef.current;
      if (currentPin.length < 6) {
        const newPin = currentPin + val;
        setUserPin(newPin);
        
        // Auto-verify if PIN length reaches 4-6 depending on backend config
        // In our backend schema we allow 4 to 6 digit pins. Let's auto-submit at 6, or let them click "OK"
        if (newPin.length === 6) {
          // Add a minor lag for visual feedback, then verify
          if (pinTimeoutRef.current) clearTimeout(pinTimeoutRef.current);
          pinTimeoutRef.current = setTimeout(() => {
            verifyUserPin(newPin);
          }, 300);
        }
      }
    }
  }, [verifyUserPin]);

  // --- ENTER BUTTON HANDLING ---
  // Uses ref for userPin to keep this callback stable across re-renders (iPad optimization)
  const handleEnterPress = useCallback(() => {
    if (userPinRef.current.length >= 4 && userPinRef.current.length <= 6) {
      verifyUserPin(userPinRef.current);
    }
  }, [verifyUserPin]);

  // --- CLOCK IN / CLOCK OUT ACTIONS ---
  const handleClockAction = useCallback(async (action: 'in' | 'out') => {
    setLoading(true);
    setErrorMsg('');
    try {
      const client = getKioskClient(kioskToken);
      const endpoint = action === 'in' ? '/api/kiosk/clock-in' : '/api/kiosk/clock-out';
      
      const response = await client.post(endpoint, {
        userId: verifiedUser.id,
        notes,
      });

      if (response.data?.success) {
        setActionSuccessMsg(
          action === 'in'
            ? t('kiosk.ui.clockInSuccess', { firstName: verifiedUser.firstName })
            : t('kiosk.ui.clockOutSuccess', { firstName: verifiedUser.firstName })
        );
        // On checkout, calculate and store the shift duration for display
        if (action === 'out' && response.data?.data?.entry?.checkIn && response.data?.data?.entry?.checkOut) {
          setShiftDuration(formatShiftDuration(
            response.data.data.entry.checkIn,
            response.data.data.entry.checkOut,
          ));
        } else {
          setShiftDuration('');
        }
        setStep('SUCCESS');
        
        // Auto reset to PIN step after 3.5 seconds
        if (clockActionTimeoutRef.current) clearTimeout(clockActionTimeoutRef.current);
        clockActionTimeoutRef.current = setTimeout(() => {
          resetKiosk();
        }, 3500);
      }
    } catch (err: any) {
      setErrorMsg(getKioskErrorMessage(err, t, 'clockAction'));
    } finally {
      setLoading(false);
    }
  }, [kioskToken, verifiedUser, notes, t]);

  const resetKiosk = useCallback(() => {
    setUserPin('');
    setVerifiedUser(null);
    setNotes('');
    setErrorMsg('');
    setShiftDuration('');
    setStep('PIN');
  }, []);

  const getInitials = (user: any) => {
    return `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  };

  // --------------------------------------------------------------------------
  // RENDER: ACTIVATION SCREEN
  // --------------------------------------------------------------------------
  if (!isActivated) {
    return (
      <div
        className="min-h-screen bg-slate-950 text-white flex flex-col justify-between p-8 font-sans select-none relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 15% 10%, rgba(49, 46, 129, 0.45), transparent 60%), ' +
            'radial-gradient(ellipse 80% 60% at 85% 90%, rgba(6, 78, 59, 0.35), transparent 60%), ' +
            '#020617',
        }}
      >
        {/* Background Decorative Gradients */}
        <div className="absolute top-[-30%] left-[-20%] w-[80%] h-[80%] rounded-full bg-indigo-900/30 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-30%] right-[-20%] w-[80%] h-[80%] rounded-full bg-emerald-900/20 blur-[120px] pointer-events-none" />

        {/* Floating error overlay — slides down from top, auto-dismisses, no layout space */}
        <KioskErrorOverlay message={errorMsg} visible={errorVisible} top="5.5rem" />

        {/* Header */}
        <div className="flex items-center justify-between z-20">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-indigo-500">Task<span className="text-white">Fly</span></span>
            <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider pl-2 border-l border-slate-800">{t('kiosk.ui.kioskBadge')}</span>
          </div>
          <div className="flex items-center gap-4">
            <KioskLanguageSelector />
            <span className="inline-block px-3 py-1 bg-red-950/40 border border-red-500/20 rounded-full text-red-400 text-xs font-semibold uppercase tracking-wider animate-pulse">{t('kiosk.ui.deviceNotActivated')}</span>
          </div>
        </div>

        {/* Central Content */}
        <div className="max-w-md mx-auto w-full z-10">
          <div className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl text-center space-y-6">
            <div className="h-16 w-16 bg-indigo-950 text-indigo-400 rounded-2xl flex items-center justify-center mx-auto border border-indigo-500/10 shadow-inner">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">{t('kiosk.ui.activateKioskIpad')}</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                {t('kiosk.ui.activationInstructions')}
              </p>
            </div>

            <form onSubmit={handleActivate} className="space-y-6">
              <div className="flex justify-center space-x-2">
                {[...Array(6)].map((_, i) => {
                  const digit = activationCode[i] || '';
                  return (
                    <div
                      key={i}
                      className={`w-12 h-14 rounded-xl border flex items-center justify-center text-2xl font-bold transition-all duration-200 ${
                        digit
                          ? 'border-indigo-500 bg-indigo-950/40 text-white shadow-lg shadow-indigo-500/15'
                          : 'border-slate-800 bg-slate-950/50 text-slate-600'
                      }`}
                    >
                      {digit}
                    </div>
                  );
                })}
              </div>

              {/* Activation Keypad (iPad optimization) */}
              <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto pt-2">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(num => (
                  <button
                    key={num}
                    type="button"
                    onPointerDown={(e) => { e.preventDefault(); if (activationCode.length < 6) setActivationCode(prev => prev + num); }}
                    className="h-14 bg-slate-900 border border-slate-800/60 rounded-xl flex items-center justify-center text-xl font-bold active:bg-indigo-600 active:border-indigo-500 active:scale-95 transition-transform transition-colors"
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onPointerDown={(e) => { e.preventDefault(); setActivationCode(''); }}
                  className="h-14 bg-slate-950 border border-slate-900/50 rounded-xl flex items-center justify-center text-xs font-bold uppercase tracking-wider text-slate-400 active:bg-slate-800"
                >
                  {t('kiosk.ui.clear')}
                </button>
                <button
                  type="button"
                  onPointerDown={(e) => { e.preventDefault(); if (activationCode.length < 6) setActivationCode(prev => prev + '0'); }}
                  className="h-14 bg-slate-900 border border-slate-800/60 rounded-xl flex items-center justify-center text-xl font-bold active:bg-indigo-600 active:border-indigo-500"
                >
                  0
                </button>
                <button
                  type="button"
                  onPointerDown={(e) => { e.preventDefault(); setActivationCode(prev => prev.slice(0, -1)); }}
                  className="h-14 bg-slate-950 border border-slate-900/50 rounded-xl flex items-center justify-center text-slate-400 active:bg-slate-800"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.41-6.41A2 2 0 0010.83 5H19a2 2 0 012 2v10a2 2 0 01-2 2h-8.17a2 2 0 01-1.42-.59L3 12z" />
                  </svg>
                </button>
              </div>

              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); handleActivate(); }}
                disabled={loading || activationCode.length !== 6}
                className="w-full h-14 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed rounded-xl font-bold text-lg shadow-lg shadow-indigo-600/20 active:scale-[0.98] transition-transform transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <div className="h-6 w-6 border-2 border-indigo-200 border-t-white rounded-full animate-spin" />
                ) : (
                  t('kiosk.ui.activateDevice')
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-slate-500 text-xs z-10">
          {t('kiosk.ui.subdomainDetected')} <span className="font-semibold text-slate-400">{subdomain}</span> • {t('kiosk.ui.kioskAppVersion', { version: 'v1.0.0' })}
        </div>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // RENDER: MAIN CLOCK-IN APPLICATION SCREEN
  // --------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen bg-slate-950 text-white flex flex-col justify-between font-sans select-none relative overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 15% 10%, rgba(49, 46, 129, 0.40), transparent 60%), ' +
          'radial-gradient(ellipse 80% 60% at 85% 90%, rgba(6, 78, 59, 0.30), transparent 60%), ' +
          '#020617',
      }}
    >
      {/* Background Glows */}
      <div className="absolute top-[-30%] left-[-20%] w-[80%] h-[80%] rounded-full bg-indigo-900/20 blur-[120px] pointer-events-none kiosk-gpu-layer" />
      <div className="absolute bottom-[-30%] right-[-20%] w-[80%] h-[80%] rounded-full bg-emerald-900/15 blur-[120px] pointer-events-none kiosk-gpu-layer" />

      {/* Floating error overlay — slides down from top, auto-dismisses, no layout space */}
      <KioskErrorOverlay message={errorMsg} visible={errorVisible} top="5.5rem" />

      {/* HEADER */}
      <header className="p-6 md:p-8 bg-slate-900/40 border-b border-slate-900/80 backdrop-blur-md kiosk-backdrop-frozen flex items-center justify-between z-20">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="text-xl font-bold text-indigo-500">Task<span className="text-white">Fly</span></span>
            <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider pl-2 border-l border-slate-800">{t('kiosk.ui.kioskBadge')}</span>
          </div>
          <div className="pl-3 border-l border-slate-700">
            <h1 className="font-extrabold text-sm tracking-wide uppercase text-slate-200">{orgName}</h1>
            <p className="text-slate-400 text-[10px] font-semibold tracking-wider uppercase">{t('kiosk.ui.timeClockKiosk')}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <KioskLanguageSelector />
          <KioskClock paused={step === 'PIN' && userPin.length > 0} language={currentLang} />
        </div>
      </header>

      {/* CENTRAL WORKSPACE */}
      <main className="flex-1 flex items-center justify-center p-6 z-10">
        {/* STEP 1: PIN ENTER SCREEN */}
        {step === 'PIN' && (
          <div className="max-w-md w-full space-y-4">
            <div className="text-center space-y-2">
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">{t('kiosk.ui.enterYourPin')}</h2>
              <p className="text-slate-400 text-sm">{t('kiosk.ui.enterPinInstructions')}</p>
            </div>

            {/* PIN Dots Screen */}
            <PinDots length={userPin.length} />

            {/* Tactile Keypad */}
            <div className="bg-slate-900/40 border border-slate-800/60 backdrop-blur-lg kiosk-backdrop-frozen kiosk-composite-isolate rounded-3xl p-5 shadow-xl">
              <KioskKeypad onPress={handleKeypadPress} disabled={loading || step !== 'PIN'} />

              {/* Memoized Enter button — only re-renders when disabled/loading changes, not on every keypress */}
              <KioskEnterButton onPress={handleEnterPress} disabled={loading || userPin.length < 4} loading={loading} label={t('kiosk.ui.enter')} />
            </div>
          </div>
        )}

        {/* STEP 2: USER VERIFIED / CHECK-IN OR CHECK-OUT ACTION */}
        {step === 'USER_CONFIRM' && verifiedUser && (
          <div className="max-w-xl w-full bg-slate-900/50 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl space-y-6 relative">
            
            {/* Go Back Header */}
            <button
              onPointerDown={(e) => { e.preventDefault(); resetKiosk(); }}
              className="absolute top-6 left-6 text-slate-400 hover:text-white flex items-center space-x-1.5 text-sm font-medium active:scale-95 transition-transform transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>{t('kiosk.ui.back')}</span>
            </button>

            {/* User Profile Overview */}
            <div className="text-center pt-4 space-y-4">
              <div className="inline-flex justify-center">
                {verifiedUser.avatar ? (
                  <img
                    src={verifiedUser.avatar}
                    alt={verifiedUser.firstName}
                    className="h-24 w-24 rounded-full border-4 border-indigo-500/20 object-cover shadow-lg"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full border-4 border-indigo-500/20 bg-indigo-950 flex items-center justify-center text-3xl font-extrabold text-indigo-300 shadow-lg">
                    {getInitials(verifiedUser)}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <h2 className="text-3xl font-extrabold tracking-tight">{verifiedUser.firstName} {verifiedUser.lastName}</h2>
                <p className="text-slate-400 text-sm">{verifiedUser.email}</p>
              </div>

              {/* Status Badge */}
              <div className="inline-flex items-center space-x-2 py-1 px-4 rounded-full bg-slate-950 border border-slate-800/60 text-sm font-semibold">
                <span className={`h-2.5 w-2.5 rounded-full ${isCheckedIn ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                <span>{isCheckedIn ? t('kiosk.ui.currentlyCheckedIn') : t('kiosk.ui.currentlyClockedOut')}</span>
              </div>
            </div>

            {/* Optional Clock-in Notes */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block">{t('kiosk.ui.commentsOrNotesOptional')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={isCheckedIn ? t('kiosk.ui.checkoutNotesPlaceholder') : t('kiosk.ui.checkinNotesPlaceholder')}
                className="w-full h-20 bg-slate-950 border border-slate-800 rounded-xl p-3 text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500 transition-all resize-none text-sm leading-relaxed"
                maxLength={500}
              />
            </div>

            {/* Large Clock In / Out Action Button */}
            <div className="space-y-3">
              {isCheckedIn ? (
                <button
                  onPointerDown={(e) => { e.preventDefault(); handleClockAction('out'); }}
                  disabled={loading}
                  className="w-full h-16 bg-rose-600 hover:bg-rose-500 disabled:bg-slate-800 disabled:text-slate-600 rounded-2xl font-bold text-lg shadow-lg shadow-rose-950/20 active:scale-[0.98] transition-transform transition-colors flex items-center justify-center space-x-2.5 text-white"
                >
                  {loading ? (
                    <div className="h-6 w-6 border-2 border-rose-200 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>{t('kiosk.ui.clockOutEndShift')}</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  onPointerDown={(e) => { e.preventDefault(); handleClockAction('in'); }}
                  disabled={loading}
                  className="w-full h-16 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-600 rounded-2xl font-bold text-lg shadow-lg shadow-emerald-950/20 active:scale-[0.98] transition-transform transition-colors flex items-center justify-center space-x-2.5 text-white"
                >
                  {loading ? (
                    <div className="h-6 w-6 border-2 border-emerald-200 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>{t('kiosk.ui.clockInStartShift')}</span>
                    </>
                  )}
                </button>
              )}

              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); resetKiosk(); }}
                className="w-full h-12 bg-slate-950 hover:bg-slate-900 border border-slate-850 rounded-xl font-medium text-slate-400 hover:text-slate-200 transition-transform transition-colors"
              >
                {t('kiosk.ui.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: SUCCESS CELEBRATION SCREEN */}
        {step === 'SUCCESS' && (
          <div className="max-w-md w-full bg-slate-900/60 border border-emerald-500/20 backdrop-blur-xl rounded-3xl p-10 shadow-2xl text-center space-y-6 animate-scaleUp">
            
            {/* Animated Success Check */}
            <div className="h-24 w-24 bg-emerald-950 border border-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-inner shadow-emerald-900/30 scale-105 transition-all">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 animate-drawCheck" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <div className="space-y-3">
              <h2 className="text-3xl font-extrabold tracking-tight text-emerald-400">{t('kiosk.ui.success')}</h2>
              <p className="text-slate-200 text-lg leading-relaxed font-semibold">
                {actionSuccessMsg}
              </p>
            </div>

            {/* Shift Duration Display — only shown after checkout (clock-out) */}
            {shiftDuration && (
              <div className="py-4 px-6 bg-slate-950/60 border border-emerald-500/15 rounded-2xl space-y-1">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  {t('kiosk.ui.workedTime')}
                </p>
                <p className="text-5xl font-extrabold font-mono text-emerald-300 tracking-tight">
                  {shiftDuration}
                </p>
              </div>
            )}

            <div className="text-xs text-slate-400 tracking-wide uppercase pt-4 border-t border-slate-800">
              {t('kiosk.ui.autoCloseMessage')}
            </div>
          </div>
        )}
      </main>

      {/* FOOTER / LOGOUT DEVICE FOR ADMINS */}
      <footer className="p-6 text-center text-slate-500 text-xs flex justify-between items-center bg-slate-900/10 border-t border-slate-900/60 z-10">
        <span>{t('kiosk.ui.kioskAppVersionFooter', { version: 'v1.0.0' })}</span>
        <button
          onPointerDown={(e) => { e.preventDefault(); setDeactivatePin(''); setShowDeactivateModal(true); }}
          className="text-slate-600 hover:text-slate-400 active:scale-95 transition-transform transition-colors"
        >
          {t('kiosk.ui.deactivateKiosk')}
        </button>
      </footer>

      {showDeactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onPointerDown={(e) => { e.preventDefault(); setShowDeactivateModal(false); }}>
          <div className="bg-slate-800 rounded-xl p-6 mx-4 max-w-sm w-full" onPointerDown={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-3">{t('kiosk.ui.deactivateKiosk')}</h3>
            <p className="text-sm text-slate-300 mb-4">{t('kiosk.ui.deactivateInstructions')}</p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={deactivatePin}
              onChange={(e) => setDeactivatePin(e.target.value)}
              placeholder={t('kiosk.ui.adminPin')}
              className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white text-center text-xl tracking-widest mb-4 focus:outline-none focus:border-emerald-500"
              autoFocus
            />
            <div className="flex gap-3">
              <button onPointerDown={(e) => { e.preventDefault(); setShowDeactivateModal(false); }} className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors">{t('kiosk.ui.cancel')}</button>
              <button onPointerDown={(e) => {
                e.preventDefault();
                if (deactivatePin === '999999' || deactivatePin === 'deactivate') {
                  localStorage.removeItem(`kiosk_token_${subdomain}`);
                  localStorage.removeItem(`kiosk_org_${subdomain}`);
                  window.location.reload();
                } else if (deactivatePin) {
                  setErrorMsg(t('kiosk.ui.incorrectAdminPin'));
                  setDeactivatePin('');
                }
              }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors">{t('kiosk.ui.deactivate')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default KioskPage;
